import BookIndex from "components/shared/book/list";
import SharedReactTable from "components/shared/reactTable";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { dateTimeToView } from "helpers/date";
import { featureModuleTypes, organizationFlags } from "helpers/enums";
import { paths } from "helpers/paths";
import { BooleanColumnFilter, DateRangeColumnFilter, FilterProps, SelectColumnFilter } from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import React, { FC } from "react";
import { Link } from "react-router-dom";
import { blockOrganization, unblockOrganization } from "sources/admin";

const featureModules = Object.assign({}, ...featureModuleTypes.map((x) => ({ [x.value]: x.label })));

class AdminOrganizationsList extends BookIndex {
    get tableColumns() {
        return [
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            {
                Header: "Название",
                accessor: "name",
                filterable: true,
                Cell: ({ value, row }: { value: string; row: { original: { id: number } } }) => (
                    <Link to={paths.adminOrganizationsShow(row.original.id)}>{value}</Link>
                ),
            },
            {
                Header: "Пин-код",
                accessor: "pinCode",
                filterable: false,
                sortable: false,
            },
            {
                Header: "ИНН",
                accessor: "inn",
                filterable: true,
            },
            {
                Header: "Флаги",
                accessor: "flags",
                sortable: false,
                filterable: true,
                filterType: "flags",
                Filter: (props: FilterProps) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            options: organizationFlags,
                            inputProps: { name: "value" },
                            propName: "label",
                            components: { ValueContainer },
                        }}
                    />
                ),
                Cell: ({ value }: { value: string }) => (
                    <div>
                        {value
                            .split(", ")
                            .map((flag) => enumLabel(organizationFlags, flag))
                            .join(", ")}
                    </div>
                ),
                CSVExport: ({ value }: { value: string }) =>
                    value
                        .split(", ")
                        .map((flag) => enumLabel(organizationFlags, flag))
                        .join(", "),
            },
            {
                Header: "Модули",
                accessor: "featureModuleTypes",
                sortable: false,
                filterable: true,
                filterType: "arrayContains",
                Filter: (props: FilterProps) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            options: featureModuleTypes,
                            inputProps: { name: "value" },
                            propName: "label",
                        }}
                    />
                ),
                Cell: ({ value }: { value: string[] }) => <div>{value.map((x) => featureModules[x]).join(", ")}</div>,
                CSVExport: ({ value }: { value: string[] }) => value.map((x) => featureModules[x]).join(", "),
            },
            {
                Header: "Самостоятельная регистрация",
                accessor: "isSelfRegistration",
                width: 150,
                sortable: true,
                filterable: true,
                filterType: "boolean",
                Filter: (props: FilterProps) => {
                    const defaultValue = { id: "isSelfRegistration", value: "all", filterType: "boolean" };
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Да", value: "true" },
                        { label: "Нет", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} defaultValue={defaultValue} />;
                },
                Cell: ({ value }: { value: boolean }) => <div className="text-center">{value ? "Да" : "Нет"}</div>,
                CSVExport: ({ value }: { value: boolean }) => (value ? "Да" : "Нет"),
            },
            {
                Header: "Зарегистрирована через ATI",
                accessor: "registeredViaAtiSu",
                filterable: true,
                filterType: "boolean",
                Filter: (props: FilterProps) => {
                    const defaultValue = { id: "registeredViaAtiSu", value: "all", filterType: "boolean" };
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Да", value: "true" },
                        { label: "Нет", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} defaultValue={defaultValue} />;
                },
                Cell: ({ value }: { value: boolean }) => <div className="text-center">{value ? "Да" : "Нет"}</div>,
                CSVExport: ({ value }: { value: boolean }) => (value ? "Да" : "Нет"),
            },
            {
                Header: "Дата блокировки",
                accessor: "blockedAt",
                filterable: true,
                Filter: DateRangeColumnFilter,
                filterType: "dateTimeRange",
                Cell: ({ value, row }: { value: string; row: { original: { id: number } } }) => {
                    const { isRoot, isIntegrationManager } = this.props.roles;
                    const toggleBlockedAt = () => {
                        this.props.modal({
                            name: "confirm",
                            title: `${value ? "Разблокировать" : "Заблокировать"} компанию?`,
                            onSuccessAction: () => this.props.mergeParams(),
                            action: () =>
                                value
                                    ? unblockOrganization({ organizationId: row.original.id })
                                    : blockOrganization({ organizationId: row.original.id }),
                            buttons: { confirm: "Да", cancel: "Нет" },
                        });
                    };
                    return (
                        <>
                            <div>{dateTimeToView(value, "DD.MM.YYYY HH:mm:ss")}</div>
                            {(isRoot || isIntegrationManager) &&
                                (value ? (
                                    <div className="btn btn-xs btn-outline-success" onClick={toggleBlockedAt}>
                                        Разблокировать
                                    </div>
                                ) : (
                                    <div className="btn btn-xs btn-outline-danger" onClick={toggleBlockedAt}>
                                        Заблокировать
                                    </div>
                                ))}
                        </>
                    );
                },
                CSVExport: ({ value }: { value: string }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            {
                Header: "Удалена",
                accessor: "isDeleted",
                width: 150,
                sortable: true,
                filterable: true,
                filterType: "boolean",
                Filter: (props: FilterProps) => {
                    const defaultValue = { id: "isDeleted", value: "false", filterType: "boolean" };
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Удаленные", value: "true" },
                        { label: "Не удаленные", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} defaultValue={defaultValue} />;
                },
                Cell: ({ value }: { value: boolean }) => <div className="text-center">{value ? "Да" : "Нет"}</div>,
                CSVExport: ({ value }: { value: boolean }) => (value ? "Да" : "Нет"),
            },
        ];
    }

    renderCreateButton = () => {
        if (!this.props.roles.isRoot && !this.props.roles.isIntegrationManager) {
            return null;
        }
        return (
            <>
                <Link className="btn btn-secondary btn-sm mr-2" to={paths.adminOrganizationSettings}>
                    <small className="icon-uEA0C-gear icon-inline mr-1" />
                    Настройки
                </Link>
                <Link className="btn btn-brand btn-sm" to={paths.adminOrganizationsNew}>
                    Добавить организацию
                </Link>
            </>
        );
    };

    renderListComponent = () => <SharedReactTable {...this.props} columns={this.state.tableColumns} />;
}

export default tableContainer(AdminOrganizationsList as unknown as FC, "adminorganizations");
